import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CalculationType = "voltage" | "current" | "resistance";

export default function OhmsLawCalculator() {
  const [calcType, setCalcType] = useState<CalculationType>("voltage");
  const [value1, setValue1] = useState("");
  const [value2, setValue2] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateResult = () => {
    const v1 = parseFloat(value1);
    const v2 = parseFloat(value2);

    if (isNaN(v1) || isNaN(v2) || v1 <= 0 || v2 <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    let calculated: number;
    switch (calcType) {
      case "voltage":
        calculated = v1 * v2; // V = I × R
        break;
      case "current":
        calculated = v1 / v2; // I = V / R
        break;
      case "resistance":
        calculated = v1 / v2; // R = V / I
        break;
    }

    setResult(calculated);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setValue1("");
    setValue2("");
    setResult(null);
  };

  const getLabels = () => {
    switch (calcType) {
      case "voltage":
        return { label1: "Current (A)", label2: "Resistance (Ω)", unit: "V" };
      case "current":
        return { label1: "Voltage (V)", label2: "Resistance (Ω)", unit: "A" };
      case "resistance":
        return { label1: "Voltage (V)", label2: "Current (A)", unit: "Ω" };
    }
  };

  const labels = getLabels();

  return (
    <PageLayout 
      calculatorName="Ohm's Law Calculator" 
      calculatorPath="/calculator/ohms-law"
      category="physics"
      subcategory="Electricity"
    >
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Ohm's Law Calculator</h1>
            <p className="text-muted-foreground">
              Calculate voltage, current, or resistance using Ohm's Law
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formula: V = I × R</CardTitle>
              <CardDescription>
                Where V is voltage (volts), I is current (amperes), and R is resistance (ohms)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="calc-type">Calculate</Label>
                  <Select value={calcType} onValueChange={(v) => setCalcType(v as CalculationType)}>
                    <SelectTrigger id="calc-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voltage">Voltage (V)</SelectItem>
                      <SelectItem value="current">Current (I)</SelectItem>
                      <SelectItem value="resistance">Resistance (R)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="value1">{labels.label1}</Label>
                  <Input
                    id="value1"
                    type="number"
                    step="0.01"
                    placeholder={`Enter ${labels.label1.toLowerCase()}`}
                    value={value1}
                    onChange={(e) => setValue1(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="value2">{labels.label2}</Label>
                  <Input
                    id="value2"
                    type="number"
                    step="0.01"
                    placeholder={`Enter ${labels.label2.toLowerCase()}`}
                    value={value2}
                    onChange={(e) => setValue2(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculateResult} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <>
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(4)} {labels.unit}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {calcType === "voltage" && "Voltage"}
                        {calcType === "current" && "Current"}
                        {calcType === "resistance" && "Resistance"}
                      </p>
                    </CardContent>
                  </Card>

                  <CalculatorSuperCalcCTA config={calculatorCTAs["ohms-law"]} trigger={result} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Select what you want to calculate</p>
                  <p>2. Enter the two known values</p>
                  <p>3. Click Calculate to get the result</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 12V with 6Ω resistance = 2A current
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
